import React, { useState } from 'react';
import { Box, ButtonV2, TextV2 } from '@withjoy/joykit';
import { ArrowLeft } from '@withjoy/joykit/icons';
import { Link, useRouterHelper, useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { routePaths } from '@apps/card/serviceCenter/route.paths';
import { MessagesV2StateProvider, useMessagesV2State } from './MessagesV2State/MessagesV2State';
import { SearchForm } from './components/SearchForm/SearchForm';
import { PostFilters, DEFAULT_FILTERS } from './components/PostFilters/PostFilters';
import { MessagesTable } from './components/MessagesTable/MessagesTable';
import { MessagesV2TableActions } from './components/TableActions/MessagesV2TableActions';
import { ResendActionBar } from './components/ResendSelection/ResendActionBar';
import { ResendConfirmModal } from './components/ResendConfirmModal/ResendConfirmModal';
import type { PostFilterValues } from './MessagesV2.types';
import { deriveAttemptNumbers, foldToLatestAttempt } from './MessagesV2.utils';
import { FAIL_STATUSES, SEARCH_COUNT_CAP } from './MessagesV2.constants';

const MessagesV2Content: React.FC = () => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  const routeHelpers = useRouterHelper();
  const { messages, totalSearchCount, loading, error, called, page, pageSize, changePage, refetch, selectedIds, toggleSelected, toggleSelectAll, clearSelection, deselect } =
    useMessagesV2State();
  const [filters, setFilters] = useState<PostFilterValues>(DEFAULT_FILTERS);
  const { isSuperAdmin, isCurrentUserSettled } = useAuth();
  const [isResendOpen, setIsResendOpen] = useState(false);

  // Paired with the settle flag, as ServiceCenterState does: `isSuperAdmin` is false while the auth
  // query is in flight, so reading it alone makes the checkbox column appear late and shift every
  // other column once auth resolves.
  const canResend = isCurrentUserSettled && isSuperAdmin;

  const filteredMessages = messages.filter(m => {
    if (filters.channel !== 'all' && m.type !== filters.channel) return false;
    if (filters.status !== 'all' && m.rawStatus !== filters.status) return false;
    if (filters.mtype !== 'all' && m.messageType !== filters.mtype) return false;
    if (filters.hasError && !FAIL_STATUSES.has(m.rawStatus)) return false;
    if (filters.recipient) {
      const q = filters.recipient.toLowerCase();
      const r = (m.recipient.email || m.recipient.phoneNumber || '').toLowerCase();
      if (!r.includes(q)) return false;
    }
    return true;
  });

  // One row per message, showing the latest attempt — an original sitting next to its own resend
  // reads as a duplicate. The CSV export below is deliberately left unfolded, so the full
  // per-attempt record stays available.
  //
  // Runs AFTER the filters above, and the order carries weight: in a failed-only view the
  // successful resend is filtered out first, so its failed original has nothing to be folded
  // behind and still shows. Folding first would hide the failure from the filter built to find it.
  const visibleMessages = foldToLatestAttempt(filteredMessages);
  // Numbered over the whole page, NOT the filtered rows: which attempt a row is belongs to its own
  // send history, not to whatever the operator is currently looking at. Numbering after the filters
  // let a status filter strip a group's earlier attempts and drag the survivor's number down with
  // them, so a third attempt exported as 2. Unfolded for the same reason — the fold drops exactly
  // the earlier attempts that make the surviving row attempt 2 or 3.
  const attemptNumberById = deriveAttemptNumbers(messages);

  // Only rows still on screen can be acted on, so a selection that scrolled out of view via a
  // filter change — or was folded behind a newer attempt — cannot be resent by surprise.
  const selectedMessages = visibleMessages.filter(message => selectedIds.has(message.id));

  const showResults = called && !loading && messages.length > 0;
  // Interpolated rather than written out so the separator follows the locale like the uncapped
  // branch does, and so a translator can move the `+` if it belongs elsewhere.
  const totalDisplay = totalSearchCount >= SEARCH_COUNT_CAP ? tq`${SEARCH_COUNT_CAP.toLocaleString()}+` : totalSearchCount.toLocaleString();
  const totalPages = Math.ceil(totalSearchCount / pageSize);
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  return (
    <Box
      as="main"
      paddingX={{ _: '1rem', md: '2.5rem' }}
      overflowX="hidden"
    >
      <Box
        display="grid"
        rowGap="1.5rem"
        maxWidth="1440px"
        marginX="auto"
        marginTop={10}
      >
        {/* Back button — same markup as Messages.tsx */}
        <ButtonV2
          as={Link}
          to={routeHelpers.buildPath(routePaths.servicecenter.path)}
          variant="link"
          fullWidth={false}
          justifySelf="self-start"
        >
          <ArrowLeft
            size="md"
            marginRight="0.5rem"
          />{' '}
          {tq`Back to Service Center`}
        </ButtonV2>

        {/* Page header */}
        <Box>
          <Box
            display="flex"
            alignItems="center"
            gap="10px"
            marginBottom="4px"
          >
            <TextV2 typographyVariant="hed2">{tq`Messages v2`}</TextV2>
            <Box
              display="inline-block"
              paddingX="8px"
              paddingY="2px"
              borderRadius="100px"
              background="rgb(238,235,255)"
              fontSize="11px"
              fontWeight={700}
              color="#4951EF"
            >
              {tq`BETA`}
            </Box>
          </Box>
          <TextV2
            typographyVariant="body1"
            color="mono10"
          >
            {tq`Search internal message records across email and SMS channels`}
          </TextV2>
        </Box>

        <SearchForm />

        {showResults && (
          <>
            <PostFilters
              messages={messages}
              filters={filters}
              onChange={setFilters}
            />

            <MessagesV2TableActions
              filteredMessages={filteredMessages}
              attemptNumberById={attemptNumberById}
              loading={loading}
            />

            <ResendActionBar
              selectedCount={canResend ? selectedMessages.length : 0}
              onResend={() => setIsResendOpen(true)}
              onClear={clearSelection}
              disabled={loading}
            />

            {/* Result count + pagination */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <TextV2
                typographyVariant="label3"
                color="mono10"
              >
                {tq`Showing ${visibleMessages.length.toLocaleString()} of ${totalDisplay} results`}
              </TextV2>

              {totalPages > 1 && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="8px"
                >
                  <ButtonV2
                    size="sm"
                    variant="ghost"
                    disabled={!hasPrev || loading}
                    onClick={() => changePage(page - 1)}
                  >
                    {tq`← Prev`}
                  </ButtonV2>
                  <TextV2
                    typographyVariant="label3"
                    color="mono10"
                  >
                    {page} / {totalPages}
                  </TextV2>
                  <ButtonV2
                    size="sm"
                    variant="ghost"
                    disabled={!hasNext || loading}
                    onClick={() => changePage(page + 1)}
                  >
                    {tq`Next →`}
                  </ButtonV2>
                </Box>
              )}
            </Box>
          </>
        )}

        <MessagesTable
          messages={visibleMessages}
          attemptNumberById={attemptNumberById}
          loading={loading}
          called={called}
          error={error}
          canResend={canResend}
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          onToggleSelectAll={toggleSelectAll}
        />

        <ResendConfirmModal
          isOpen={isResendOpen}
          messages={selectedMessages}
          onClose={() => setIsResendOpen(false)}
          onCompleted={resentIds => {
            // Only the rows that actually sent are unticked. An `errored` row wrote nothing on the
            // backend, so once its tick is gone nothing identifies it as still needing a send; a
            // `failed` row did write a row but still needs retrying. Leaving both ticked means the
            // action bar reads "2 messages selected" and the retry the dialog recommends is one
            // click, instead of the operator re-ticking everything and double-sending what worked.
            deselect(resentIds);
            // New resend rows only appear after a refetch.
            refetch();
          }}
        />
      </Box>
    </Box>
  );
};

export const MessagesV2: React.FC = () => (
  <MessagesV2StateProvider>
    <MessagesV2Content />
  </MessagesV2StateProvider>
);
